import { ActivityType } from '../service/ActivityFeedStorageItem';
import {
    ActivityFeedItem,
    LikeActivityFeedItem,
    MentionActivityFeedItem,
    UserAtMentionedActivityFeedItem,
    FlightReservationActivityFeedItem,
    ParcelDeliveryActivityFeedItem,
    YammerLiveEventActivityFeedItem,
    ReactionActivityFeedItem,
} from '../service/ActivityFeedItem';
import {
    activityFeedV2LikeCardBody,
    activityFeedYammerLiveEventNotificationTitle,
    activityFeedV2TwoOrMoreLikesCardBody,
    activityFeedV2MentionCardBody,
    activityFeedV2FlightReservationCardBody,
    activityFeedV2FlightHeroActionText,
    activityFeedV2ParcelDeliveryCardBody,
    activityFeedV2ParcelHeroActionText,
    activityFeedV2ReactionCardBody,
    activityFeedV2TwoReactionsCardBody,
    activityFeedV2ThreeOrMoreReactionsCardBody,
} from './activityFeedCard.locstring.json';
import { ActivityFeedActionType, OpenLinkActionType } from '../components/ActivityFeedActions';
import { ControlIcons } from 'owa-control-icons';
import loc, { format } from 'owa-localize';
import { PersonaCardBehaviorProps } from 'owa-persona/lib/components/PersonaCardBehavior';
import {
    formatDayAtTime,
    formatMonthDay,
    formatUserTime,
    formatWeekDay,
    parse,
    differenceInSeconds,
    isWithinRange,
    addDays,
} from 'owa-datetime';
import abbreviatedFormatDuration from 'owa-duration-formatter/lib/abbreviated';
import { getUserConfiguration } from 'owa-session-store';
import { getFileTypeIconProps } from '@uifabric/file-type-icons';
import tryCreateEntityItem from 'txp-data/lib/utils/data/tryCreateEntityItem';
import FlightReservation from 'txp-data/lib/schema/FlightReservation';
import ParcelDelivery from 'txp-data/lib/schema/ParcelDelivery';
import { getTaskInsightFeedCard } from './MyAnalyticsFeedCardPresentationDetails';
import { getRealTimeFlightUpdateCard } from './RealTimeFlightUpdateCardPresentationDetails';
import { observableNow } from 'owa-observable-datetime';
import { getOutOfRoutineFeedCard } from './OutOfRoutineCardPresentationDetails';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { IsFileActivityPayload } from './ActivityFeedHelpers';

export interface ActivityFeedCardDetailsProps {
    item: ActivityFeedCardDetails;
    onAction: (itemId: string, action: ActivityFeedActionType) => void;
    itemId: string;
}

export interface ActivityFeedCardDetails {
    senderInfo: PersonaCardBehaviorProps[];
    internetMessageId?: string;
    messageItemId?: string;
    cardTitle: string;
    cardSubTitle1?: string;
    cardSubTitle1CssStyle?: any;
    cardSubTitle2?: string;
    cardIcon: string;
    isSeen: boolean;
    timeStamp: Date;
    cardType: ActivityCardLayoutType;
    activityType: ActivityType;
    heroButtonProperties?: HeroActionProperties[];
    cardOnClickAction?: [ActivityFeedActionType];
    dismissAction?(): void;
}

export interface HeroActionProperties {
    buttonTitle: string;
    buttonActionUrl?: string;
    buttonActionServiceConfig?: HeroActionServiceCallConfiguration;
    heroActionName: string;
}

export interface HeroActionServiceCallConfiguration {
    url: string;
    method: string;
    body?: string;
}

export enum ActivityCardLayoutType {
    SingleTitleActivityCard = 0,
    MultiTitleActivityCard = 1,
    HeroActionCard = 2,
}

export function getActivityCardPresentationDetails(item: ActivityFeedItem) {
    if (item.payload) {
        switch (item.type) {
            case ActivityType.Like:
                return getLikeFeedCard(item);
            case ActivityType.AtMention:
                return getAtMentionFeedCard(item);
            case ActivityType.UserAtMentioned:
                return getUserAtMentionFeedCard(item);
            case ActivityType.TxpFlightReservation:
                return getFlightReservationFeedCard(item);
            case ActivityType.TxpParcelDelivery:
                return getParcelDeliveryFeedCard(item);
            case ActivityType.TaskNotificationInsight:
            case ActivityType.FollowUpNotificationInsight:
                return getTaskInsightFeedCard(item);
            case ActivityType.OutOfRoutineNotificationInsight:
                return getOutOfRoutineFeedCard(item);
            case ActivityType.RealTimeFlightUpdate:
                return getRealTimeFlightUpdateCard(item);
            case ActivityType.YammerLiveEventNotification:
                return getYammerLiveEventFeedCard(item);
            case ActivityType.Reactions:
                return getReactionFeedCard(item);
        }
    }
    return {} as ActivityFeedCardDetails;
}

function getYammerLiveEventFeedCard(
    item: YammerLiveEventActivityFeedItem
): ActivityFeedCardDetails {
    const eventName = item.payload.liveEventInfo.eventName;
    let title = format(loc(activityFeedYammerLiveEventNotificationTitle), eventName);
    title = item.isSeen ? title : formatBoldMarkdownString(title);
    const communityName = item.payload.liveEventInfo.communityName;
    const cardOnClickAction = [
        {
            actionName: 'Action.OpenUrl',
            url: item.payload.liveEventInfo.eventUrl,
            itemId: item.id,
        } as OpenLinkActionType,
    ];
    return {
        senderInfo: [{ name: communityName, emailAddress: '' }],
        cardTitle: title,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardIcon: ControlIcons.YammerLogo,
        activityType: ActivityType.YammerLiveEventNotification,
        cardType: ActivityCardLayoutType.MultiTitleActivityCard,
        cardSubTitle2: communityName,
        cardOnClickAction: cardOnClickAction,
    } as ActivityFeedCardDetails;
}

function getFlightReservationFeedCard(
    item: FlightReservationActivityFeedItem
): ActivityFeedCardDetails {
    const flightData = tryCreateEntityItem(item.payload) as FlightReservation;
    if (flightData) {
        const flightTime = parse(
            getUserConfiguration().UserOptions.TimeZone,
            flightData.departureTime.toString()
        );
        if (item.type === ActivityType.TxpFlightReservation) {
            // Only show card if flight departs within the next 24 hours.
            // Later this logic will be updated using data from TEE.
            if (!isWithinRange(flightTime, observableNow(), addDays(observableNow(), 1))) {
                return {} as ActivityFeedCardDetails;
            }
        }
        const destinationCity = flightData.arrivalAirport.city;
        const flightDepartureDiffFromNow = differenceInSeconds(flightTime, observableNow());
        const flightDepartsIn = abbreviatedFormatDuration(flightDepartureDiffFromNow, {
            maxUnits: 2,
        });
        const flightNumber = flightData.flightNumber;
        let titleText = format(
            loc(activityFeedV2FlightReservationCardBody),
            flightNumber,
            destinationCity,
            flightDepartsIn
        );
        titleText = item.isSeen ? titleText : formatBoldMarkdownString(titleText);
        const flightFormatTime = formatDayAtTime(
            formatWeekDay(flightTime) + ', ' + formatMonthDay(flightTime),
            formatUserTime(flightTime)
        );
        return {
            senderInfo: [{ name: flightData.airline?.name, emailAddress: '' }],
            internetMessageId: flightData.originalEmailInternetMessageId,
            messageItemId: flightData.originalEmailEwsId,
            cardTitle: titleText,
            timeStamp: item.timestamp,
            isSeen: item.isSeen,
            cardType: ActivityCardLayoutType.MultiTitleActivityCard,
            activityType: ActivityType.TxpFlightReservation,
            cardIcon: ControlIcons.AirplaneSolid,
            cardSubTitle1: flightFormatTime,
            heroButtonProperties: [
                {
                    buttonTitle: loc(activityFeedV2FlightHeroActionText),
                    buttonActionUrl: flightData.checkinUrl,
                } as HeroActionProperties,
            ],
        } as ActivityFeedCardDetails;
    } else {
        return {} as ActivityFeedCardDetails;
    }
}

function getParcelDeliveryFeedCard(item: ParcelDeliveryActivityFeedItem): ActivityFeedCardDetails {
    const parcelData = tryCreateEntityItem(item.payload) as ParcelDelivery;
    if (parcelData) {
        const deliveryTimestamp = formatWeekDay(
            parse(getUserConfiguration().UserOptions.TimeZone, parcelData.expectedArrivalUntil)
        );
        let titleText = format(loc(activityFeedV2ParcelDeliveryCardBody), deliveryTimestamp);
        titleText = item.isSeen ? titleText : formatBoldMarkdownString(titleText);
        return {
            senderInfo: [{ name: parcelData.provider?.name, emailAddress: '' }],
            internetMessageId: parcelData.originalEmailInternetMessageId,
            messageItemId: parcelData.originalEmailEwsId,
            cardTitle: titleText,
            timeStamp: item.timestamp,
            isSeen: item.isSeen,
            cardType: ActivityCardLayoutType.SingleTitleActivityCard,
            activityType: ActivityType.TxpParcelDelivery,
            cardIcon: ControlIcons.DeliveryTruck,
            heroButtonProperties: [
                {
                    buttonTitle: loc(activityFeedV2ParcelHeroActionText),
                    buttonActionUrl: parcelData.trackingUrl,
                } as HeroActionProperties,
            ],
        } as ActivityFeedCardDetails;
    } else {
        return {} as ActivityFeedCardDetails;
    }
}

function getLikeFeedCard(item: LikeActivityFeedItem): ActivityFeedCardDetails {
    let titleText = '';
    const encodedSubject = item.payload.TargetSubject ?? item.payload.TargetMessagePreview;
    if (item.payload.Actors.length == 1) {
        titleText = format(
            loc(activityFeedV2LikeCardBody),
            getFirstName(item.payload.Actors[0].EmailAddress.Name)
        );
    } else {
        titleText = format(
            loc(activityFeedV2TwoOrMoreLikesCardBody),
            item.payload.Actors.length.toString()
        );
    }
    var senderInfo = item.payload.Actors.map(function (actor) {
        return { name: actor.EmailAddress.Name, emailAddress: actor.EmailAddress.EmailAddress };
    });
    titleText = item.isSeen ? titleText : formatBoldMarkdownString(titleText);
    return {
        senderInfo: senderInfo,
        internetMessageId: item.payload.TargetLogicalId,
        messageItemId: item.payload.TargetItemId,
        cardTitle: titleText,
        cardSubTitle2: encodedSubject,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.SingleTitleActivityCard,
        activityType: ActivityType.Like,
        cardIcon: ControlIcons.Like,
    } as ActivityFeedCardDetails;
}

function getReactionFeedCard(item: ReactionActivityFeedItem): ActivityFeedCardDetails {
    let titleText = '';
    switch (item.payload.TotalReactorCount) {
        case 0:
            // should not happen because if we got here we should have at least one valid reactor
            return {} as ActivityFeedCardDetails;
        case 1:
            titleText = format(loc(activityFeedV2ReactionCardBody), item.payload.FirstReactorName);
            break;
        case 2:
            titleText = format(
                loc(activityFeedV2TwoReactionsCardBody),
                item.payload.FirstReactorName
            );
            break;
        default:
            titleText = format(
                loc(activityFeedV2ThreeOrMoreReactionsCardBody),
                item.payload.FirstReactorName,
                item.payload.TotalReactorCount - 1
            );
    }

    titleText = item.isSeen ? titleText : formatBoldMarkdownString(titleText);
    return {
        senderInfo: [
            {
                name: item.payload.FirstReactorName,
                emailAddress: item.payload.FirstReactorEmail,
            },
        ],
        internetMessageId: item.payload.InternetMessageId,
        messageItemId: item.payload.MessageId,
        cardTitle: titleText,
        cardSubTitle1: item.payload.Subject,
        cardSubTitle2: item.payload.Preview,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.MultiTitleActivityCard,
        activityType: ActivityType.Reactions,
        cardIcon: item.payload.FirstReactorType,
    } as ActivityFeedCardDetails;
}

function getAtMentionFeedCard(item: MentionActivityFeedItem): ActivityFeedCardDetails {
    const encodedSubject = item.payload.Subject;
    let cardTitle = format(
        loc(activityFeedV2MentionCardBody),
        getFirstName(item.payload.Actor.EmailAddress?.Name)
    );
    cardTitle = item.isSeen == true ? cardTitle : formatBoldMarkdownString(cardTitle);
    let ewsId = convertRestIdToEwsId(item.payload.MessageId);
    return {
        senderInfo: [
            {
                name: item.payload.Actor.EmailAddress?.Name,
                emailAddress: item.payload.Actor.EmailAddress?.EmailAddress,
            },
        ],
        internetMessageId: item.payload.InternetMessageId,
        messageItemId: ewsId,
        cardTitle: cardTitle,
        cardSubTitle2: encodedSubject,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.MultiTitleActivityCard,
        activityType: ActivityType.AtMention,
        cardIcon: ControlIcons.Accounts,
    } as ActivityFeedCardDetails;
}

export function formatBoldMarkdownString(text: string): any {
    let trimmedText = text.trim();
    if (trimmedText == '') {
        return trimmedText;
    } else {
        return `**${trimmedText}**`;
    }
}

function getUserAtMentionFeedCard(item: UserAtMentionedActivityFeedItem): ActivityFeedCardDetails {
    if (IsFileActivityPayload(item.payload)) {
        return getFileActivityFeedCard(item);
    }

    const encodedFilename = item.payload.SourceFileName;
    let cardTitle = format(
        loc(activityFeedV2MentionCardBody),
        getFirstName(item.payload.Mentioner.DisplayName)
    );
    cardTitle = item.isSeen == true ? cardTitle : formatBoldMarkdownString(cardTitle);

    const fileIcon = {
        ...getFileTypeIconProps({
            extension: item.payload.SourceFileExtension,
            size: 16,
            imageFileType: 'png',
        }),
    };

    const cardOnClickAction = [
        {
            actionName: 'Action.OpenUrl',
            url: item.payload.ObjectId + '?web=1',
            itemId: item.id,
            internetMessageId: item.payload.ObjectId,
        } as OpenLinkActionType,
    ];
    return {
        senderInfo: [
            {
                name: item.payload.Mentioner.DisplayName,
                emailAddress: item.payload.Mentioner.Email,
            },
        ],
        internetMessageId: item.payload.ObjectId,
        cardTitle: cardTitle,
        cardSubTitle2: encodedFilename,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.SingleTitleActivityCard,
        activityType: ActivityType.UserAtMentioned,
        cardIcon: fileIcon.iconName,
        cardOnClickAction: cardOnClickAction,
    } as ActivityFeedCardDetails;
}

function getFileActivityFeedCard(item: ActivityFeedItem): ActivityFeedCardDetails {
    if (!IsFileActivityPayload(item.payload)) {
        return null;
    }

    const encodedFilename = item.payload.fileDetails.sourceFileName;
    let cardTitle = format(
        loc(activityFeedV2MentionCardBody),
        getFirstName(item.payload.actor.name)
    );
    cardTitle = item.isSeen == true ? cardTitle : formatBoldMarkdownString(cardTitle);

    const fileIcon = {
        ...getFileTypeIconProps({
            extension: item.payload.fileDetails.sourceFileExtension,
            size: 16,
            imageFileType: 'png',
        }),
    };

    const cardOnClickAction = [
        {
            actionName: 'Action.OpenUrl',
            url: item.payload.fileDetails.linkUrl
                ? item.payload.fileDetails.linkUrl
                : item.payload.fileDetails.documentUrl + '?web=1',
            itemId: item.id,
            internetMessageId: item.payload.fileDetails.documentUrl,
        } as OpenLinkActionType,
    ];
    return {
        senderInfo: [
            {
                name: item.payload.actor.name,
                emailAddress: item.payload.actor.email,
            },
        ],
        internetMessageId: item.payload.fileDetails.documentUrl,
        cardTitle: cardTitle,
        cardSubTitle2: encodedFilename,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.SingleTitleActivityCard,
        activityType: ActivityType.UserAtMentioned,
        cardIcon: fileIcon.iconName,
        cardOnClickAction: cardOnClickAction,
    } as ActivityFeedCardDetails;
}

function getFirstName(displayName: String): String {
    return displayName.split(' ')[0];
}
